<template>
    <Modal v-if="isOpen" @cancel="cancel">
        <div
            class="modal-command-card"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-command-title"
            tabindex="-1"
            ref="modalCard"
            @keydown.esc.stop.prevent="cancel"
        >
            <!-- Header -->
            <div class="modal-command-header">
                <h2 id="modal-command-title" class="header-title">
                    {{ $t('devis.modal.command.title', { id }) }}
                </h2>
                <button
                    type="button"
                    class="close-button"
                    :aria-label="$t('devis.modal.command.btn.cancel')"
                    @click="cancel"
                    :disabled="isLoading"
                >
                    <img class="close-icon" :src="closeIcon" alt="" aria-hidden="true" />
                </button>
            </div>

            <!-- Body -->
            <div class="modal-command-body">
                <!-- Message d'erreur global (ex: retour API) -->
                <div v-if="errorMessage" class="error-banner" role="alert">
                    <span class="error-banner-text">{{ errorMessage }}</span>
                </div>

                <!-- Récapitulatif du devis -->
                <div class="recap-card" :aria-label="$t('devis.modal.recap')">
                    <div class="recap-item" v-if="objet">
                        <span class="recap-label">{{ $t('dataList.devisObject') }}</span>
                        <span class="recap-value recap-bold">{{ objet }}</span>
                    </div>
                    <div class="recap-item">
                        <span class="recap-label">{{ $t('dataList.devisId') }}</span>
                        <span class="recap-value">{{ $t('devisValidation.devisNumber') }}{{ id }}</span>
                    </div>
                    <div class="recap-item" v-if="formattedPrice">
                        <span class="recap-label">{{ $t('dataList.devisPrice') }}</span>
                        <span class="recap-value recap-price">{{ formattedPrice }} HT</span>
                    </div>
                </div>

                <!-- Cas avec signature électronique possible -->
                <div v-if="isElectronicSigningPossible" class="section-container">
                    <h3 class="section-title" id="choice-group-label">
                        {{ $t('devis.modal.command.choice.title') }}
                    </h3>

                    <div
                        class="choice-cards-container"
                        role="radiogroup"
                        aria-labelledby="choice-group-label"
                    >
                        <!-- Choix 1: Signer électroniquement -->
                        <div
                            class="choice-card"
                            :class="{ selected: finalizationChoice === 'signature' }"
                            role="radio"
                            :aria-checked="finalizationChoice === 'signature'"
                            tabindex="0"
                            @click="selectChoice('signature')"
                            @keydown.space.prevent="selectChoice('signature')"
                            @keydown.enter.prevent="selectChoice('signature')"
                            @keydown.down.prevent="selectChoice('withoutSign')"
                            @keydown.up.prevent="selectChoice('signature')"
                        >
                            <div class="choice-radio-indicator">
                                <RadioBox :selected="finalizationChoice === 'signature'" />
                            </div>
                            <div class="choice-text">
                                <span class="choice-title">{{ $t('devis.modal.command.choice.sign.title') }}</span>
                                <span class="choice-desc">{{ $t('devis.modal.command.choice.sign.desc') }}</span>
                            </div>
                        </div>

                        <!-- Choix 2: Commander sans signer en ligne -->
                        <div
                            class="choice-card"
                            :class="{ selected: finalizationChoice === 'withoutSign' }"
                            role="radio"
                            :aria-checked="finalizationChoice === 'withoutSign'"
                            tabindex="0"
                            @click="selectChoice('withoutSign')"
                            @keydown.space.prevent="selectChoice('withoutSign')"
                            @keydown.enter.prevent="selectChoice('withoutSign')"
                            @keydown.down.prevent="selectChoice('withoutSign')"
                            @keydown.up.prevent="selectChoice('signature')"
                        >
                            <div class="choice-radio-indicator">
                                <RadioBox :selected="finalizationChoice === 'withoutSign'" />
                            </div>
                            <div class="choice-text">
                                <span class="choice-title">{{ $t('devis.modal.command.choice.withoutSign.title') }}</span>
                                <span class="choice-desc">{{ $t('devis.modal.command.choice.withoutSign.desc') }}</span>
                            </div>
                        </div>
                    </div>

                    <!-- Section Formulaire du signataire (uniquement si choix == signature) -->
                    <div v-if="finalizationChoice === 'signature'" class="signatory-section">
                        <h3 class="section-title">
                            {{ $t('devis.modal.command.sign.title') }}
                        </h3>

                        <div class="signatory-form">
                            <!-- Nom -->
                            <div class="form-field">
                                <label for="signatory-lastname" class="field-label">
                                    {{ $t('label.lastname') }}
                                    <span class="required-mention" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="signatory-lastname"
                                    type="text"
                                    class="custom-text-input"
                                    :class="{ 'input-error': attemptedSubmit && lastnameError }"
                                    v-model="lastname"
                                    @input="validateLastname"
                                    :disabled="isLoading"
                                    required
                                    aria-required="true"
                                    :aria-invalid="attemptedSubmit && !!lastnameError"
                                    :aria-describedby="attemptedSubmit && lastnameError ? 'signatory-lastname-error' : undefined"
                                />
                                <span
                                    v-if="attemptedSubmit && lastnameError"
                                    id="signatory-lastname-error"
                                    class="field-error-message"
                                    role="alert"
                                >
                                    {{ lastnameError }}
                                </span>
                            </div>

                            <!-- Prénom -->
                            <div class="form-field">
                                <label for="signatory-firstname" class="field-label">
                                    {{ $t('label.firstname') }}
                                    <span class="required-mention" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="signatory-firstname"
                                    type="text"
                                    class="custom-text-input"
                                    :class="{ 'input-error': attemptedSubmit && firstnameError }"
                                    v-model="firstname"
                                    @input="validateFirstname"
                                    :disabled="isLoading"
                                    required
                                    aria-required="true"
                                    :aria-invalid="attemptedSubmit && !!firstnameError"
                                    :aria-describedby="attemptedSubmit && firstnameError ? 'signatory-firstname-error' : undefined"
                                />
                                <span
                                    v-if="attemptedSubmit && firstnameError"
                                    id="signatory-firstname-error"
                                    class="field-error-message"
                                    role="alert"
                                >
                                    {{ firstnameError }}
                                </span>
                            </div>

                            <!-- Email -->
                            <div class="form-field">
                                <label for="signatory-email" class="field-label">
                                    {{ $t('label.email') }}
                                    <span class="required-mention" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="signatory-email"
                                    type="email"
                                    class="custom-text-input"
                                    :class="{ 'input-error': attemptedSubmit && emailError }"
                                    v-model="email"
                                    @input="validateEmail"
                                    :disabled="isLoading"
                                    required
                                    aria-required="true"
                                    :aria-invalid="attemptedSubmit && !!emailError"
                                    :aria-describedby="attemptedSubmit && emailError ? 'signatory-email-error' : 'signatory-email-help'"
                                />
                                <span id="signatory-email-help" class="field-help-message">
                                    {{ $t('devis.modal.command.sign.emailHelp') }}
                                </span>
                                <span
                                    v-if="attemptedSubmit && emailError"
                                    id="signatory-email-error"
                                    class="field-error-message"
                                    role="alert"
                                >
                                    {{ emailError }}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Section Informations de commande (si commande sans signature électronique) -->
                <div v-if="!isSignChoice" class="section-container without-sign-info">
                    <div class="order-info-grid">
                        <!-- Champ N° de commande -->
                        <div class="form-field">
                            <label for="numero-commande-input" class="field-label">
                                {{ $t('devis.modal.command.numero.commande') }}
                                <span class="optional-mention">({{ $t('devis.modal.command.optional') }})</span>
                            </label>
                            <input
                                id="numero-commande-input"
                                type="text"
                                class="custom-text-input"
                                v-model="numeroCommande"
                                :placeholder="$t('devis.modal.command.numero.commande')"
                                :disabled="isLoading"
                            />
                        </div>

                        <!-- Champ Bon de commande (PDF) -->
                        <div class="form-field">
                            <span class="field-label" id="bon-commande-label">
                                {{ $t('devis.modal.command.bonCommande.label') }}
                                <span class="optional-mention">({{ $t('devis.modal.command.optional') }})</span>
                            </span>

                            <div class="file-upload-wrapper">
                                <input
                                    ref="fileInputRef"
                                    id="bon-commande-file-input"
                                    type="file"
                                    class="hidden-file-input"
                                    accept=".pdf,application/pdf"
                                    @change="onFileChange"
                                    :disabled="isLoading"
                                    aria-labelledby="bon-commande-label"
                                    :aria-invalid="!!fileError"
                                    :aria-describedby="fileError ? 'bon-commande-error' : undefined"
                                />

                                <div v-if="!currentFile" class="file-action-row">
                                    <button
                                        type="button"
                                        class="btn-add-file"
                                        @click="triggerFileInput"
                                        :disabled="isLoading"
                                    >
                                        <img :src="clipIcon" class="clip-icon" alt="" aria-hidden="true" />
                                        <span>{{ $t('devis.modal.command.bonCommande.add') }}</span>
                                    </button>
                                </div>

                                <div v-else class="file-selected-row">
                                    <RemovableItem
                                        :title="currentFile.name"
                                        @remove="removeFile"
                                    />
                                </div>
                            </div>

                            <span
                                v-if="fileError"
                                id="bon-commande-error"
                                class="field-error-message"
                                role="alert"
                            >
                                {{ fileError }}
                            </span>
                        </div>
                    </div>

                    <p class="confirmation-mention">
                        {{ $t('devis.modal.command.withoutSign.confirmText') }}
                    </p>
                </div>
            </div>

            <!-- Footer / Actions -->
            <div class="modal-command-footer">
                <button
                    type="button"
                    class="btn-action btn-cancel"
                    @click="cancel"
                    :disabled="isLoading"
                >
                    {{ $t('devis.modal.command.btn.cancel') }}
                </button>

                <button
                    type="button"
                    class="btn-action btn-primary"
                    @click="handlePrimaryAction"
                    :disabled="isLoading || (attemptedSubmit && isSignChoice && !isSignFormValid)"
                >
                    <span v-if="isLoading" class="loader-spinner" aria-hidden="true"></span>
                    <span>{{ primaryButtonLabel }}</span>
                </button>
            </div>
        </div>
    </Modal>
</template>

<script lang="ts">
import { defineComponent, PropType, ref } from 'vue';
import Modal from '@/components/ui-components/Modal.vue';
import RemovableItem from '@/components/ui-components/RemovableItem.vue';
import RadioBox from '@/components/ui-components/RadioBox.vue';
import currencyFormatter from 'currency-formatter';
import { DevisAsigner } from '../services/DevisService';
import { DEVIS } from '../store/mutation-types';

import closeIcon from '@/assets/img/close-white.svg';
import clipIcon from '@/assets/img/clip-icon.svg';

const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024; // 10 Mo
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FinalizationChoice = 'signature' | 'withoutSign';

export default defineComponent({
    name: 'ModalCommand',
    components: {
        Modal,
        RemovableItem,
        RadioBox,
    },
    props: {
        id: {
            type: String,
            required: true,
        },
        isOpen: {
            type: Boolean,
            required: true,
        },
        isElectronicSigningPossible: {
            type: Boolean,
            required: true,
        },
        objet: {
            type: String,
            default: '',
        },
        montant: {
            type: [Number, String] as PropType<number | string | null>,
            default: null,
        },
        monnaie: {
            type: String,
            default: 'EUR',
        },
        initialNumCommande: {
            type: String,
            default: '',
        },
        isLoading: {
            type: Boolean,
            default: false,
        },
        errorMessage: {
            type: String,
            default: '',
        },
    },
    emits: {
        cancel: () => true,
        commanderDevis: (numeroCommande: string) => typeof numeroCommande === 'string',
        commanderDevisSignatureElectronique: (devisAsigner: DevisAsigner) => !!devisAsigner,
    },
    setup() {
        const fileInputRef = ref<HTMLInputElement | null>(null);
        const modalCard = ref<HTMLDivElement | null>(null);

        return {
            fileInputRef,
            modalCard,
            closeIcon,
            clipIcon,
        };
    },
    data() {
        return {
            numeroCommande: this.initialNumCommande || '',
            finalizationChoice: 'signature' as FinalizationChoice,
            lastname: '',
            firstname: '',
            email: '',
            fileError: '',
            lastnameError: '',
            firstnameError: '',
            emailError: '',
            attemptedSubmit: false,
        };
    },
    computed: {
        currentFile(): File | undefined {
            const files = this.$store.getters.getBonCommande;
            return files && files.length > 0 ? files[0] : undefined;
        },
        formattedPrice(): string {
            if (this.montant !== null && this.montant !== undefined && this.montant !== '') {
                const numeric = typeof this.montant === 'string' ? parseFloat(this.montant) : this.montant;
                if (!isNaN(numeric)) {
                    return currencyFormatter.format(numeric, { code: this.monnaie || 'EUR' });
                }
            }
            return '';
        },
        isSignChoice(): boolean {
            return this.isElectronicSigningPossible && this.finalizationChoice === 'signature';
        },
        isSignFormValid(): boolean {
            return !this.lastnameError && !this.firstnameError && !this.emailError &&
                !!this.lastname.trim() && !!this.firstname.trim() && !!this.email.trim();
        },
        primaryButtonLabel(): string {
            if (this.isSignChoice) {
                return this.$t('devis.modal.command.btn.continueToSign');
            }
            return this.$t('devis.modal.command.btn.confirmOrder');
        },
    },
    watch: {
        isOpen(opened: boolean) {
            if (opened) {
                this.initData();
                this.$nextTick(() => {
                    this.modalCard?.focus();
                });
            }
        },
        initialNumCommande(newVal: string) {
            if (!this.numeroCommande) {
                this.numeroCommande = newVal || '';
            }
        },
    },
    mounted() {
        if (this.isOpen) {
            this.initData();
        }
    },
    methods: {
        initData() {
            this.attemptedSubmit = false;
            this.fileError = '';
            if (this.initialNumCommande && !this.numeroCommande) {
                this.numeroCommande = this.initialNumCommande;
            }
            this.finalizationChoice = this.isElectronicSigningPossible ? 'signature' : 'withoutSign';
            this.initUserInfo();
        },
        initUserInfo() {
            const userInfo = this.$store.getters.getUserInformations;
            if (userInfo) {
                if (!this.lastname && userInfo.nom) this.lastname = userInfo.nom;
                if (!this.firstname && userInfo.prenom) this.firstname = userInfo.prenom;
                if (!this.email && (userInfo.email || userInfo.email_azure)) {
                    this.email = userInfo.email || userInfo.email_azure;
                }
            }
            this.validateLastname();
            this.validateFirstname();
            this.validateEmail();
        },
        selectChoice(choice: FinalizationChoice) {
            this.finalizationChoice = choice;
            if (choice === 'signature') {
                this.validateSignForm();
            }
        },
        triggerFileInput() {
            if (this.isLoading) return;
            this.fileInputRef?.click();
        },
        onFileChange(event: Event) {
            const target = event.target as HTMLInputElement;
            const file = target.files?.[0];
            if (!file) return;

            const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');
            if (!isPdf) {
                this.fileError = this.$t('devis.modal.command.bonCommande.error.format');
                target.value = '';
                return;
            }

            if (file.size > MAX_FILE_SIZE_BYTES) {
                this.fileError = this.$t('devis.modal.command.bonCommande.error.size');
                target.value = '';
                return;
            }

            this.fileError = '';
            this.$store.commit(DEVIS.ADD_BONCOMMANDE, file);
            target.value = '';
        },
        removeFile() {
            this.fileError = '';
            this.$store.commit(DEVIS.REMOVE_BONCOMMANDE);
            if (this.fileInputRef) {
                this.fileInputRef.value = '';
            }
        },
        validateLastname() {
            this.lastnameError = (!this.lastname || !this.lastname.trim())
                ? this.$t('devis.modal.command.error.lastnameRequired')
                : '';
        },
        validateFirstname() {
            this.firstnameError = (!this.firstname || !this.firstname.trim())
                ? this.$t('devis.modal.command.error.firstnameRequired')
                : '';
        },
        validateEmail() {
            const trimmed = this.email ? this.email.trim() : '';
            if (!trimmed) {
                this.emailError = this.$t('devis.modal.command.error.emailRequired');
            } else if (!EMAIL_REGEX.test(trimmed)) {
                this.emailError = this.$t('devis.modal.command.error.emailInvalid');
            } else {
                this.emailError = '';
            }
        },
        validateSignForm(): boolean {
            this.validateLastname();
            this.validateFirstname();
            this.validateEmail();
            return this.isSignFormValid;
        },
        handlePrimaryAction() {
            if (this.isLoading) return;

            if (this.isSignChoice) {
                this.attemptedSubmit = true;
                if (!this.validateSignForm()) {
                    return;
                }

                const devisAsigner = new DevisAsigner(
                    0,
                    this.email.trim(),
                    this.lastname.trim(),
                    this.firstname.trim()
                );
                this.$emit('commanderDevisSignatureElectronique', devisAsigner);
            } else {
                this.$emit('commanderDevis', this.numeroCommande ? this.numeroCommande.trim() : '');
            }
        },
        cancel() {
            if (this.isLoading) return;
            this.$emit('cancel');
        },
    },
});
</script>

<style scoped>
.modal-command-card {
    background-color: var(--white);
    border-radius: 6px;
    overflow: hidden;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    width: 90vw;
    max-width: 680px;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    outline: none;
}

.modal-command-header {
    background-color: var(--bleu-prinicipal);
    padding: 14px 20px;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.header-title {
    margin: 0;
    font-size: var(--font-size-medium, 18px);
    font-weight: var(--font-weight-medium, 500);
    color: var(--white);
}

.close-button {
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-button:disabled {
    cursor: not-allowed;
    opacity: 0.6;
}

.close-icon {
    width: 16px;
    height: 16px;
}

.modal-command-body {
    padding: 20px;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 18px;
    max-height: calc(90vh - 130px);
}

.error-banner {
    background-color: var(--rouge-error-background, #fff5f5);
    border: 1px solid var(--rouge-error-border, #fecaca);
    border-radius: 4px;
    padding: 10px 14px;
}

.error-banner-text {
    color: var(--rouge-error-titre, #b91c1c);
    font-size: var(--font-size-small, 14px);
    font-weight: var(--font-weight-medium, 500);
}

/* Récapitulatif */
.recap-card {
    background-color: var(--background-principal, #f5f7ff);
    border-radius: 6px;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 6px;
    border-left: 4px solid var(--bleu-secondaire, #2E4DE6);
}

.recap-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: var(--font-size-small, 14px);
}

.recap-label {
    color: var(--color-principal, #424B54);
}

.recap-value {
    color: var(--dark-grey, #373737);
}

.recap-bold {
    font-weight: var(--font-weight-bold, 700);
}

.recap-price {
    font-weight: var(--font-weight-bold, 700);
    color: var(--bleu-prinicipal, #121A7A);
    font-size: var(--font-size-low-medium, 16px);
}

/* Section */
.section-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.section-title {
    margin: 0;
    font-size: var(--font-size-low-medium, 16px);
    font-weight: var(--font-weight-bold, 700);
    color: var(--dark-grey, #373737);
}

/* Grille champs de commande */
.order-info-grid {
    display: flex;
    gap: 16px;
    width: 100%;
}

.form-field {
    flex: 1 1 0;
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.field-label {
    font-size: var(--font-size-small, 14px);
    font-weight: var(--font-weight-medium, 500);
    color: var(--dark-grey, #373737);
}

.optional-mention {
    font-size: var(--font-size-very-small, 12px);
    font-weight: var(--font-weight-normal, 400);
    color: var(--medium-dark-grey, #9E9E9E);
    margin-left: 4px;
}

.required-mention {
    color: var(--red, #D73026);
    margin-left: 2px;
    font-weight: var(--font-weight-bold, 700);
}

.custom-text-input {
    height: 38px;
    border: 1px solid var(--medium-dark-grey, #9E9E9E);
    border-radius: 4px;
    padding: 0 12px;
    font-size: var(--font-size-small, 14px);
    color: var(--black, #000);
    outline: none;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
    width: 100%;
    box-sizing: border-box;
    font-family: inherit;
}

.custom-text-input:focus {
    border-color: var(--bleu-secondaire, #2E4DE6);
    box-shadow: 0 0 0 2px rgba(46, 77, 230, 0.2);
}

.custom-text-input.input-error {
    border-color: var(--red, #D73026);
}

.custom-text-input:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
}

.field-error-message {
    color: var(--red, #D73026);
    font-size: var(--font-size-very-small, 12px);
    line-height: 1.2;
}

.field-help-message {
    color: var(--medium-dark-grey, #9E9E9E);
    font-size: var(--font-size-very-small, 12px);
    line-height: 1.2;
}

/* Fichier Bon de commande */
.file-upload-wrapper {
    display: flex;
    align-items: center;
    min-height: 38px;
}

.hidden-file-input {
    display: none;
}

.file-action-row {
    display: flex;
    align-items: center;
}

.btn-add-file {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background-color: var(--white);
    border: 1px solid var(--bleu-secondaire, #2E4DE6);
    color: var(--bleu-secondaire, #2E4DE6);
    padding: 8px 14px;
    border-radius: 4px;
    font-size: var(--font-size-small, 14px);
    font-weight: var(--font-weight-medium, 500);
    cursor: pointer;
    transition: background-color 0.2s, color 0.2s;
}

.btn-add-file:hover:not(:disabled) {
    background-color: rgba(46, 77, 230, 0.08);
}

.btn-add-file:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.clip-icon {
    width: 16px;
    height: 16px;
}

.file-selected-row {
    display: flex;
    align-items: center;
}

/* Cartes de choix signature */
.choice-cards-container {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.choice-card {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    padding: 12px 14px;
    border: 1.5px solid var(--medium-light-grey, #E0E0E0);
    border-radius: 6px;
    background-color: var(--white);
    cursor: pointer;
    transition: border-color 0.2s ease, background-color 0.2s ease;
    outline: none;
}

.choice-card:hover {
    border-color: var(--bleu-secondaire, #2E4DE6);
    background-color: rgba(46, 77, 230, 0.02);
}

.choice-card:focus-visible {
    border-color: var(--bleu-secondaire, #2E4DE6);
    box-shadow: 0 0 0 2px rgba(46, 77, 230, 0.25);
}

.choice-card.selected {
    border-color: var(--bleu-prinicipal, #121A7A);
    background-color: rgba(18, 26, 122, 0.04);
}

.choice-radio-indicator {
    padding-top: 2px;
    display: flex;
    align-items: center;
}

.choice-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.choice-title {
    font-size: var(--font-size-small, 14px);
    font-weight: var(--font-weight-bold, 700);
    color: var(--dark-grey, #373737);
}

.choice-desc {
    font-size: var(--font-size-very-small, 12px);
    color: var(--color-principal, #424B54);
}

/* Section signataire */
.signatory-section {
    margin-top: 6px;
    padding: 14px;
    background-color: var(--background-principal, #f5f7ff);
    border-radius: 6px;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.signatory-form {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

/* Cas sans signature information */
.without-sign-info {
    padding-top: 4px;
}

.confirmation-mention {
    margin: 0;
    font-size: var(--font-size-small, 14px);
    color: var(--color-principal, #424B54);
}

/* Footer & boutons */
.modal-command-footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 12px;
    padding: 14px 20px;
    border-top: 1px solid var(--medium-light-grey, #E0E0E0);
    background-color: var(--white);
}

.btn-action {
    padding: 9px 18px;
    border-radius: 4px;
    font-size: var(--font-size-small, 14px);
    font-weight: var(--font-weight-medium, 500);
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    transition: background-color 0.2s, opacity 0.2s;
    border: none;
    font-family: inherit;
}

.btn-cancel {
    background-color: var(--white);
    color: var(--dark-grey, #373737);
    border: 1px solid var(--medium-dark-grey, #9E9E9E);
}

.btn-cancel:hover:not(:disabled) {
    background-color: #f5f5f5;
}

.btn-primary {
    background-color: var(--bleu-secondaire, #2E4DE6);
    color: var(--white);
}

.btn-primary:hover:not(:disabled) {
    background-color: #243ec4;
}

.btn-action:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.loader-spinner {
    width: 14px;
    height: 14px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Responsive mobile */
@media (max-width: 768px) {
    .modal-command-card {
        width: 96vw;
        max-height: 94vh;
    }

    .modal-command-body {
        padding: 14px;
        gap: 14px;
    }

    .order-info-grid {
        flex-direction: column;
    }

    .modal-command-footer {
        flex-direction: column-reverse;
        gap: 8px;
        padding: 12px 14px;
    }

    .btn-action {
        width: 100%;
    }
}
</style>
